import { useState } from "react";
import { Box, Chip, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import {
  DEFAULT_QUICK_VIEW_DAYS,
  QUICK_VIEW_DAYS,
  QUICK_VIEW_OPTIONS,
} from "./quickViews";
import type { QuickView, QuickViewDays, QuickViewKind } from "./quickViews";
import type { FinanceStatus } from "../../../api/financialDashboard";
import { FINANCE_STATUS_OPTIONS } from "../finance/financialDashboard.utils";

const PINK = "#D175B6";

type Props = {
  value: QuickView;
  onChange: (next: QuickView) => void;
  /** "Hide duplicates" chip (2026-10-05), on by default. Omitted = not rendered. */
  showDuplicates?: boolean;
  onShowDuplicatesChange?: (next: boolean) => void;
  finance?: { hideStatuses?: boolean; status: FinanceStatus | "all"; onChange: (next: FinanceStatus | "all") => void };
};

/**
 * "Quick view" chips under the dashboard header: Unconfirmed, Upcoming (7/14/30/60
 * days) and Ongoing. One at a time; clicking the active one shows everything again.
 * Works with every other filter (they all apply together).
 */
export default function QuickViewBar({ value, onChange, showDuplicates = false, onShowDuplicatesChange, finance }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const days = value?.days ?? DEFAULT_QUICK_VIEW_DAYS;

  const toggle = (view: QuickViewKind) => onChange(value?.view === view ? null : { view, days });
  const pickDays = (d: QuickViewDays) => {
    setAnchor(null);
    onChange({ view: "upcoming", days: d });
  };

  return (
    <Box
      role="group"
      aria-label="Quick views"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px",
        padding: "0 40px 16px",
        "@media (max-width: 600px)": { padding: "0 20px 16px" },
      }}
    >
      <Typography sx={{ fontSize: "14px", opacity: 0.8, mr: "2px" }}>Quick view</Typography>
      {finance ? (finance.hideStatuses ? [] : Object.entries(FINANCE_STATUS_OPTIONS)).map(([status, option]) => <Chip key={status}
        label={option.label} clickable aria-pressed={finance.status === status}
        onClick={() => finance.onChange(finance.status === status ? "all" : status as FinanceStatus)}
        sx={{ height: 34, fontSize: "14px", fontWeight: 600,
          color: finance.status === status ? "#141C57" : "#fff",
          bgcolor: finance.status === status ? PINK : "transparent",
          border: `1px solid ${finance.status === status ? PINK : "rgba(255,255,255,0.6)"}`,
          "&:hover": { bgcolor: finance.status === status ? PINK : "rgba(209,117,182,0.18)" } }}
      />) : QUICK_VIEW_OPTIONS.map((o) => {
        const active = value?.view === o.view;
        const label = o.view === "upcoming" ? `${o.label} · ${days} days` : o.label;
        return (
          <Tooltip key={o.view} title={active ? "Click again to show everything" : o.hint} arrow describeChild>
            <Chip
              label={label}
              clickable
              aria-pressed={active}
              onClick={() => toggle(o.view)}
              {...(o.view === "upcoming"
                ? {
                    deleteIcon: <ArrowDropDown aria-label="Pick how many days" />,
                    onDelete: (e: React.SyntheticEvent) =>
                      setAnchor((e.currentTarget as HTMLElement).closest(".MuiChip-root") as HTMLElement),
                  }
                : {})}
              sx={{
                height: 34,
                fontSize: "14px",
                fontWeight: 600,
                color: active ? "#141C57" : "#fff",
                bgcolor: active ? PINK : "transparent",
                border: `1px solid ${active ? PINK : "rgba(255,255,255,0.6)"}`,
                "&:hover": { bgcolor: active ? PINK : "rgba(209,117,182,0.18)" },
                "& .MuiChip-deleteIcon": { color: active ? "#141C57" : "#fff", "&:hover": { color: active ? "#141C57" : PINK } },
              }}
            />
          </Tooltip>
        );
      })}
      {onShowDuplicatesChange ? (
        <Tooltip
          arrow
          describeChild
          title={
            showDuplicates
              ? "Duplicates are showing, struck through. Click to hide them."
              : "Duplicates are hidden. Click to show them, struck through."
          }
        >
          <Chip
            label="Hide duplicates"
            clickable
            aria-pressed={!showDuplicates}
            onClick={() => onShowDuplicatesChange(!showDuplicates)}
            sx={{
              height: 34,
              fontSize: "14px",
              fontWeight: 600,
              color: !showDuplicates ? "#141C57" : "#fff",
              bgcolor: !showDuplicates ? PINK : "transparent",
              border: `1px solid ${!showDuplicates ? PINK : "rgba(255,255,255,0.6)"}`,
              "&:hover": { bgcolor: !showDuplicates ? PINK : "rgba(209,117,182,0.18)" },
            }}
          />
        </Tooltip>
      ) : null}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {QUICK_VIEW_DAYS.map((d) => (
          <MenuItem key={d} selected={value?.view === "upcoming" && value.days === d} onClick={() => pickDays(d)}>
            Next {d} days
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
